import { storeToRefs } from 'pinia';
import { usePropertyStore } from '@/common/stores/property';
import { useViewModeStore, type ViewMode } from '@/common/stores/view-mode';
import { LOGIN_CUSTOMER, VIEW_MODE } from '@/common/define/common.define';
import { useAuthStore } from '@/common/stores/auth';
import { useSessionTimerStore } from '@/common/stores/session-timer';
import { webStorageController } from '@/common/utils/browser';
import { getViewModeByProperty, settingLanguage, setBodyClass, setMetaInfo } from './router.utils';

/**
 * viewMode 모드 원활한 테스트를 위한 코드
 * url 파라미터 viewmode=maxgauge, viewmode=intermax, ...로 viewMode 모드 테스트 가능
 */
export const initializeAppForDevTesting = async () => {
  const propertyStore = usePropertyStore();
  const { propertyData } = storeToRefs(propertyStore);
  const viewModeStore = useViewModeStore();
  const { setViewMode } = viewModeStore;
  const { productName, viewMode } = storeToRefs(viewModeStore);

  const urlSearch = new URLSearchParams(location.search);
  const urlViewMode = urlSearch.get('viewmode') as ViewMode;

  if (urlViewMode) {
    if (VIEW_MODE[urlViewMode.toUpperCase()]) {
      webStorageController.setItem({
        type: 'session',
        key: 'viewmode',
        value: urlViewMode,
      });

      setViewMode(VIEW_MODE[urlViewMode.toUpperCase()]);
    } else {
      webStorageController.removeItem({
        type: 'session',
        key: 'viewmode',
      });

      setViewMode(VIEW_MODE.EXEMONE);
    }
    propertyStore.getOptions?.loadStyle();
    setBodyClass(urlViewMode, propertyData.value?.loginCustomer || LOGIN_CUSTOMER.EXEMONE);
    setMetaInfo(
      productName.value,
      propertyData.value?.loginCustomer || LOGIN_CUSTOMER.EXEMONE,
      viewMode.value,
    );
  }
};

export const initializeApp = async () => {
  const propertyStore = usePropertyStore();
  const { propertyData } = storeToRefs(propertyStore);
  const viewModeStore = useViewModeStore();
  const { setViewMode } = viewModeStore;
  const { productName, viewMode } = storeToRefs(viewModeStore);

  // 언어 설정
  settingLanguage();

  // viewMode 설정
  setViewMode(
    getViewModeByProperty(
      (propertyData.value.viewMode?.toLocaleLowerCase() as ViewMode) || VIEW_MODE.EXEMONE,
    ),
  );

  // 스타일 설정
  propertyStore.getOptions?.loadStyle();

  // 바디 클래스 설정
  setBodyClass(
    propertyData.value.viewMode as ViewMode,
    propertyData.value?.loginCustomer || LOGIN_CUSTOMER.EXEMONE,
  );

  // 메타 정보 설정 (title, favicon)
  setMetaInfo(
    productName.value,
    propertyData.value?.loginCustomer || LOGIN_CUSTOMER.EXEMONE,
    viewMode.value,
  );

  // sessionTimeoutMode 설정
  const { fetchSessionTime, updateSessionTime } = useSessionTimerStore();
  const { tokenInfo } = storeToRefs(useAuthStore());
  const { isExpiredSessionTime } = storeToRefs(useSessionTimerStore());

  if (propertyData.value.sessionTimeoutMode) {
    await fetchSessionTime(tokenInfo.value?.sessionToken ?? '');
  }

  if (!propertyData.value.sessionTimeoutMode || !isExpiredSessionTime.value) {
    await updateSessionTime({
      refreshToken: tokenInfo.value?.refreshToken ?? '',
      sessionToken: tokenInfo.value?.sessionToken ?? '',
      accessToken: tokenInfo.value?.accessToken ?? '',
    });
  }
};
